import {
  Modal,
  Box,
  Typography,
  Button,
  Divider,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import { useState, useEffect } from "react";
import { DefaultPrimaryButton, InformationModal } from "@/styles/globalstyles";
import CloseButton from "@/components/buttons/CloseButton";
import type { BuyerBossWithBuyerCode, BuyerBoss } from "@/api/buyerBossService";
import { WarningAlert } from "@/utils/alert";

interface SaveBuyerBossModalProps {
  open: boolean;
  handleClose: () => void;
  selectedBuyerBoss: BuyerBossWithBuyerCode | null;
  buyerBossList: BuyerBoss[];
  allBuyerCodes: string[];
  existingEmployeeIds: string[];
  onSave: (employeeId: string, buyerCodes: string[]) => void;
}

const SaveBuyerBossModal = ({
  open,
  handleClose,
  selectedBuyerBoss,
  buyerBossList,
  allBuyerCodes,
  existingEmployeeIds,
  onSave,
}: SaveBuyerBossModalProps) => {
  const isEditMode = !!selectedBuyerBoss;
  const [selectedEmployee, setSelectedEmployee] = useState<BuyerBoss | null>(null);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      if (selectedBuyerBoss) {
        const matched = buyerBossList.find(
          (b) => b.user_id === selectedBuyerBoss.employee_id,
        );
        setSelectedEmployee(matched ?? null);
        setSelectedCodes(selectedBuyerBoss.buyer_code ?? []);
      } else {
        setSelectedEmployee(null);
        setSelectedCodes([]);
      }
    }
  }, [open, selectedBuyerBoss, buyerBossList]);

  const employeeFoundInList = isEditMode
    ? buyerBossList.some((b) => b.user_id === selectedBuyerBoss?.employee_id)
    : false;

  const availableBossList = isEditMode
    ? buyerBossList
    : buyerBossList.filter((b) => !existingEmployeeIds.includes(b.user_id));

  const handleSave = () => {
    if (!selectedEmployee) {
      WarningAlert("กรุณาเลือก Buyer Boss", "");
      return;
    }
    if (selectedCodes.length === 0) {
      WarningAlert("กรุณาเลือก Buyer Code อย่างน้อย 1 รายการ", "");
      return;
    }
    onSave(selectedEmployee.user_id, selectedCodes);
    handleClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{ ...InformationModal, maxHeight: "90vh" }}>
        <CloseButton onClick={handleClose} />
        <Typography variant="h2">
          {isEditMode ? "แก้ไข Buyer Manager" : "เพิ่ม Buyer Manager"}
        </Typography>
        <Box p={1}>
          <Divider sx={{ my: 1 }} />

          <Autocomplete
            value={selectedEmployee}
            onChange={(_e, value) => setSelectedEmployee(value)}
            options={availableBossList}
            getOptionLabel={(option) =>
              `${option.user_id} - ${option.name} ${option.lastname}`
            }
            isOptionEqualToValue={(option, value) =>
              option.user_id === value.user_id
            }
            disabled={isEditMode && employeeFoundInList}
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  <>
                    Buyer Manager{" "}
                    <Typography component="span" color="error.main">
                      *
                    </Typography>
                  </>
                }
                variant="outlined"
                sx={{ mt: 2 }}
              />
            )}
          />

          <Autocomplete
            multiple
            value={selectedCodes}
            onChange={(_e, value) => setSelectedCodes(value)}
            options={allBuyerCodes}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    label={option}
                    color="info"
                    size="small"
                    key={key}
                    {...tagProps}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  <>
                    Buyer Code{" "}
                    <Typography component="span" color="error.main">
                      *
                    </Typography>
                  </>
                }
                variant="outlined"
                sx={{ mt: 2 }}
              />
            )}
          />
        </Box>
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            sx={DefaultPrimaryButton}
            onClick={handleSave}
          >
            บันทึก
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SaveBuyerBossModal;
